import React, { useEffect, useState } from "react";
import Spreadsheet from "react-spreadsheet";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import VesselHeader from "../../components/Vessel/VesselHeader";
import { fetchVesselRawData } from "../../services/vesselService";
import dayjs from "dayjs";
import VoyageFilter from "../../components/Common/VoyageFilter";

// function transformTo2DArray(data) {
//   if (!Array.isArray(data) || data.length === 0) return [];

//   const filteredData = data.map((obj, index) => {
//     const { _id, __v, ID, createdAt, updatedAt, ...rest } = obj;

//     const newObj = {
//       ID: index + 1, // Add index as ID field
//       ...rest,
//     };

//     const dateKey = "Current_report_closing_time_LT (dd-mmm-yyyy hh:mm)";
//     if (newObj[dateKey]) {
//       const rawDate = newObj[dateKey];
//       const cleanedDate = rawDate.replace(/\s+/g, " ").trim();
//       const parsedDate = dayjs(
//         cleanedDate,
//         ["M/D/YYYY h:mm:ss A", "M/D/YYYY H:mm"],
//         true
//       );

//       newObj["REPORT TYPE DD/MM/YYYY"] = parsedDate.isValid()
//         ? parsedDate.format("DD/MM/YYYY")
//         : cleanedDate;

//       delete newObj[dateKey];
//     }

//     return newObj;
//   });

//   const headers = Object.keys(filteredData[0]).map((key) => ({
//     value: key.toUpperCase(),
//     readOnly: true,
//     className: "font-bold bg-blue-50 text-blue-800",
//   }));

//   const rows = filteredData.map((obj) =>
//     Object.keys(obj).map((key) => ({
//       value: obj[key],
//       readOnly: true,
//       className: "font-medium text-gray-800",
//     }))
//   );

//   return [headers, ...rows];
// }
function transformTo2DArray(data) {
  if (!Array.isArray(data) || data.length === 0) return [];

  const allFields = new Set();
  
  data.forEach((obj) => {
    const { _id, __v, createdAt, updatedAt, ...rest } = obj;
    Object.keys(rest).forEach((key) => allFields.add(key));
  });

  const fieldArray = Array.from(allFields);
  if (!fieldArray.includes("ID")) {
    fieldArray.unshift("ID");
  }

  const filteredData = data.map((obj, index) => {
    const { _id, __v, createdAt, updatedAt, ...rest } = obj;
    
    const newObj = { ID: index + 1 };
    
    fieldArray.forEach((field) => {
      if (field !== "ID") {
        newObj[field] = rest[field] !== undefined ? rest[field] : "";
      }
    });

    const dateKey = "Current_report_closing_time_LT (dd-mmm-yyyy hh:mm)";
    if (newObj[dateKey]) {
      const rawDate = newObj[dateKey];
      const cleanedDate = rawDate.replace(/\s+/g, " ").trim();
      const parsedDate = dayjs(
        cleanedDate,
        ["M/D/YYYY h:mm:ss A", "M/D/YYYY H:mm"],
        true
      );

      newObj["REPORT TYPE DD/MM/YYYY"] = parsedDate.isValid()
        ? parsedDate.format("DD/MM/YYYY")
        : cleanedDate;

      delete newObj[dateKey];
    }

    return newObj;
  });

  const headers = fieldArray.map((key) => ({
    value: key.toUpperCase(),
    readOnly: true,
    className: "font-bold bg-blue-50 text-blue-800",
  }));

  const rows = filteredData.map((obj) =>
    fieldArray.map((key) => ({
      value: obj[key] !== undefined ? obj[key] : "",
      readOnly: true,
      className: "font-medium text-gray-800",
    }))
  );

  return [headers, ...rows];
}

const VesselDataQuality = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { currentVessel } = useSelector((state) => state.vesselOverview);
  const { selectedVoyage } = useSelector((state) => state.voyageFilter);
  const [selectedVesselId, setSelectedVesselId] = useState(currentVessel?._id);

  // Update selected vessel ID when location state changes
  useEffect(() => {
    if (currentVessel?._id) {
      setSelectedVesselId(currentVessel?._id);
    }
  }, [currentVessel?._id]);

  useEffect(() => {
    if (!selectedVesselId) {
      console.warn("No vesselId found, skipping fetch.");
      return;
    }

    const fetchDataQuality = async () => {
      setLoading(true);
      try {
        const res = await fetchVesselRawData(
          selectedVesselId,
          selectedVoyage?._id
        );
        console.log("Res:", res);

        if (res.data.status === "success") {
          const transformedData = transformTo2DArray(res.data.data);
          console.log("Transformed data:", transformedData);
          setData(transformedData);
        } else {
          toast.error("Failed to load fleet data");
        }
      } catch (err) {
        console.error("Error fetching vessel data:", err);
        // toast.error("Error loading fleet data");
      } finally {
        setLoading(false);
      }
    };

    fetchDataQuality();
  }, [selectedVesselId, selectedVoyage]);

  const Spinner = () => (
    <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent">
      <span className="sr-only">Loading...</span>
    </div>
  );

  return (
    <div className="font-sans text-sm">
      <div className="mb-6 flex justify-between">
        <VesselHeader pageTitle="Data Quality" />
        <div className="flex">
          <VoyageFilter autoSelectLatest={true} />
        </div>
      </div>
      <div className="relative">
        <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
          <div className="min-w-full">
            {loading ? (
              <div className="flex items-center justify-center py-16 px-6">
                <div className="text-center">
                  <Spinner />
                  <p className="mt-4 text-gray-600 text-base">
                    Loading data...
                  </p>
                </div>
              </div>
            ) : data.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                No data available.
              </div>
            ) : (
              <div className="h-[68vh] overflow-hidden flex flex-col">
                <div className="flex-1 overflow-auto">
                  <div className="inline-block min-w-full">
                    <Spreadsheet
                      data={data || []}
                      onChange={setData}
                      hideRowIndicators={true}
                      hideColumnIndicators={true}
                      className="enhanced-spreadsheet"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style jsx global>{`
        .enhanced-spreadsheet {
          --selected-cell: #e3f2fd;
          --selected-row: #f5f5f5;
          --selected-column: #f5f5f5;
        }
        .enhanced-spreadsheet table {
          border-collapse: separate;
          border-spacing: 0;
          /* Remove width: auto !important as it causes issues */
          min-width: 100%;
          table-layout: auto;
        }
        .enhanced-spreadsheet td {
          border-right: 1px solid #e5e7eb !important;
          border-bottom: 1px solid #e5e7eb !important;
          padding: 10px 14px !important;
          transition: background-color 0.2s;
          white-space: nowrap;
        }
        .enhanced-spreadsheet td:first-child {
          border-left: 1px solid #e5e7eb !important;
        }
        .enhanced-spreadsheet th {
          background-color: #f8fafc !important;
          font-weight: 600 !important;
          color: #1e40af !important;
          border-bottom: 2px solid #e5e7eb !important;
          padding: 12px 14px !important;
          position: sticky;
          top: 0;
          z-index: 10;
          white-space: nowrap;
        }
        .enhanced-spreadsheet tr:hover td {
          background-color: #f9fafb !important;
        }
        .enhanced-spreadsheet .active-cell {
          box-shadow: inset 0 0 0 2px #3b82f6 !important;
        }
        .Spreadsheet__cell--readonly {
          color: inherit;
        }

        /* Ensure the spreadsheet container allows scrolling */
        .Spreadsheet {
          overflow: visible !important;
        }
        .Spreadsheet__table {
          min-width: 100% !important;
        }
      `}</style>
    </div>
  );
};

export default VesselDataQuality;
